import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, DollarSign, Receipt, PiggyBank,
  CreditCard, Calendar, Settings as SettingsIcon, LogOut, Menu, X, TrendingUp, Sun, Moon, MailWarning,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import useIdleLogout from "../hooks/useIdleLogout";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard",     Icon: LayoutDashboard },
  { to: "/paychecks", label: "Paychecks",     Icon: DollarSign },
  { to: "/bills",     label: "Bills",         Icon: Receipt },
  { to: "/savings",   label: "Savings Goals", Icon: PiggyBank },
  { to: "/debts",     label: "Debt Tracker",  Icon: CreditCard },
  { to: "/calendar",  label: "Calendar",      Icon: Calendar },
  { to: "/settings",  label: "Settings",      Icon: SettingsIcon },
];

function NavItem({ to, label, Icon, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
          isActive
            ? "bg-primary-600 text-white"
            : "text-slate-300 hover:bg-slate-700 hover:text-white"
        }`
      }
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {label}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { currentUser, emailVerified, logout, sendVerification, refreshUser } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [verifyMsg, setVerifyMsg]   = useState("");

  useIdleLogout();

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  async function handleResend() {
    try {
      await sendVerification();
      setVerifyMsg("Verification email sent — check your inbox.");
    } catch (e) {
      setVerifyMsg(e.message || "Could not send verification email.");
    }
  }

  async function handleCheckVerified() {
    await refreshUser();
    setVerifyMsg("");
  }

  const Sidebar = ({ onNav }) => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="flex items-center gap-2 px-4 py-5 border-b border-slate-700">
        <TrendingUp className="w-6 h-6 text-primary-400" />
        <span className="text-white font-bold text-lg">FinancePlan</span>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.to} {...item} onClick={onNav} />
        ))}
      </nav>

      {/* Dark mode toggle + user + logout */}
      <div className="px-3 py-4 border-t border-slate-700 space-y-1">
        {/* Theme toggle */}
        <button
          onClick={toggle}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
        >
          {dark
            ? <Sun  className="w-5 h-5 flex-shrink-0" />
            : <Moon className="w-5 h-5 flex-shrink-0" />
          }
          {dark ? "Light Mode" : "Dark Mode"}
        </button>

        {/* User */}
        <div className="flex items-center gap-2 px-3 py-1">
          <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {currentUser?.email?.[0]?.toUpperCase() || "U"}
          </div>
          <span className="text-slate-300 text-xs truncate">{currentUser?.email}</span>
        </div>

        {/* Sign out */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-slate-800 flex-shrink-0">
        <Sidebar onNav={() => {}} />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative flex flex-col w-64 bg-slate-800 z-50">
            <button
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
              onClick={() => setMobileOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
            <Sidebar onNav={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary-600" />
            <span className="font-bold text-gray-900 dark:text-gray-100">FinancePlan</span>
          </div>
          {/* Mobile theme toggle */}
          <button
            onClick={toggle}
            className="ml-auto p-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </header>

        {/* Email-verification banner — soft block: app is browsable, writes are blocked in api.js and Firestore rules. */}
        {currentUser && !emailVerified && (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border-b border-yellow-200 dark:border-yellow-800/60 px-4 py-3 flex flex-wrap items-center gap-2 text-sm">
            <MailWarning className="w-4 h-4 text-yellow-700 dark:text-yellow-400 flex-shrink-0" />
            <span className="text-yellow-900 dark:text-yellow-200 flex-1 min-w-0">
              {verifyMsg || "Verify your email to enable saving and syncing changes."}
            </span>
            <button onClick={handleResend} className="text-xs font-semibold text-yellow-800 dark:text-yellow-300 underline hover:no-underline">
              Resend
            </button>
            <button onClick={handleCheckVerified} className="text-xs font-semibold text-yellow-800 dark:text-yellow-300 underline hover:no-underline">
              I've verified
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
